import React, { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  BookOpen,
  ChevronRight,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { seoApi } from '../api/client';
import type { BlogSEOOverview } from '../schemas/validation';
import { useNavigate } from 'react-router-dom';
import { SEOAnalysisPanel } from './SEOAnalysisPanel';

interface BlogSEOOverviewPanelProps {
  blogId: string;
  siteId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const BlogSEOOverviewPanel: React.FC<BlogSEOOverviewPanelProps> = ({
  blogId,
  siteId,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<BlogSEOOverview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !blogId) return;

    const fetchOverview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await seoApi.getBlogOverview(blogId);
        setOverview(result);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load blog SEO overview';
        setError(errorMessage);
        console.error('Blog SEO overview error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverview();
  }, [isOpen, blogId]);

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await seoApi.getBlogOverview(blogId);
      setOverview(result);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to refresh blog SEO overview';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getStatusBadge = (status: string) => {
    if (status === 'published') {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
          Published
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
        Draft
      </span>
    );
  };

  const handleViewPostInEditor = (postId: string) => {
    navigate(`/sites/${siteId}/blog/posts/${postId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900">Blog SEO Overview</h2>
              {overview && (
                <div
                  className={`px-3 py-1 rounded-full ${getScoreBgColor(
                    overview.overview.averageScore
                  )}`}
                >
                  <span
                    className={`text-sm font-bold ${getScoreColor(overview.overview.averageScore)}`}
                  >
                    {overview.overview.averageScore}/100
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh analysis"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading && !overview && (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            )}

            {overview && (
              <div className="space-y-6">
                {/* Blog Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{overview.blogName}</h3>
                  <p className="text-sm text-gray-500">{overview.url}</p>
                </div>

                {/* Overview Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard
                    label="Total Posts"
                    value={overview.overview.totalPosts}
                    icon={<FileText className="w-5 h-5" />}
                  />
                  <MetricCard
                    label="Average Score"
                    value={`${overview.overview.averageScore}/100`}
                    icon={<TrendingUp className="w-5 h-5" />}
                    valueColor={getScoreColor(overview.overview.averageScore)}
                  />
                  <MetricCard
                    label="Total Issues"
                    value={overview.overview.totalIssues}
                    icon={<AlertCircle className="w-5 h-5" />}
                    valueColor={
                      overview.overview.totalIssues > 0 ? 'text-red-600' : 'text-green-600'
                    }
                  />
                  <MetricCard
                    label="Critical Issues"
                    value={overview.overview.criticalIssues}
                    icon={<AlertCircle className="w-5 h-5" />}
                    valueColor={
                      overview.overview.criticalIssues > 0 ? 'text-red-600' : 'text-green-600'
                    }
                  />
                </div>

                {/* Post Status Breakdown */}
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard
                    label="Published"
                    value={overview.overview.publishedPosts}
                    icon={<BookOpen className="w-5 h-5" />}
                    valueColor="text-green-600"
                  />
                  <MetricCard
                    label="Drafts"
                    value={overview.overview.draftPosts}
                    icon={<Clock className="w-5 h-5" />}
                    valueColor="text-gray-600"
                  />
                </div>

                {/* Publishing Freshness */}
                {overview.overview.publishingFreshness?.message && (
                  <div
                    className={`p-4 rounded-lg border ${
                      overview.overview.publishingFreshness.status === 'active'
                        ? 'bg-green-50 border-green-200'
                        : overview.overview.publishingFreshness.message.type === 'warning'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {overview.overview.publishingFreshness.status === 'active' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : overview.overview.publishingFreshness.message.type === 'warning' ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {overview.overview.publishingFreshness.message.message}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          {overview.overview.publishingFreshness.message.recommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Posts List */}
                {overview.posts && overview.posts.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Posts ({overview.posts.length})
                    </h3>
                    <div className="space-y-2">
                      {overview.posts.map(post => (
                        <div
                          key={post.postId}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors group"
                          onClick={() => handleViewPostInEditor(post.postId)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <BookOpen className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {post.title}
                              </p>
                              {getStatusBadge(post.status)}
                            </div>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                              <span className="truncate max-w-[200px]">{post.url}</span>
                              <span>
                                {post.issueCount} issue
                                {post.issueCount !== 1 ? 's' : ''}
                              </span>
                              {post.criticalIssues > 0 && (
                                <span className="text-red-600 font-medium">
                                  {post.criticalIssues} critical
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div
                              className={`px-2 py-1 rounded text-xs font-semibold ${getScoreBgColor(
                                post.score
                              )} ${getScoreColor(post.score)}`}
                            >
                              {post.score}/100
                            </div>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                handleViewPostInEditor(post.postId);
                              }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Edit post"
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {overview.posts && overview.posts.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No blog posts yet</h3>
                    <p className="text-gray-500">
                      Create your first blog post to see SEO analysis.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Individual Post SEO Analysis Panel */}
      {selectedPostId && (
        <SEOAnalysisPanel
          blogId={blogId}
          postId={selectedPostId}
          isOpen={!!selectedPostId}
          onClose={() => setSelectedPostId(null)}
          title="Blog Post SEO Analysis"
        />
      )}
    </>
  );
};

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  valueColor?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  valueColor = 'text-gray-900',
}) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center space-x-2 text-gray-600 mb-2">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
    </div>
  );
};
